// src/config/database/paperStocksConfig.js

export const paperStocksTableConfig = {
  // Table identification
  id: 'sf_paper_stock',
  name: 'Small Format Paper Stocks',
  apiEndpoint: '/api/admin/small-format/paper-stocks',
  
  // Column definitions for the DataTable
  columns: [
    {
      key: 'unique_id',
      label: 'Unique ID',
      sortable: true,
      render: (value) => (
        <span className="font-mono text-sm text-blue-600">{value}</span>
      )
    },
    {
      key: 'name',
      label: 'Name',
      sortable: true,
      render: (value) => (
        <span className="font-medium text-gray-900">{value}</span>
      )
    },
    {
      key: 'category',
      label: 'Category',
      sortable: true,
      render: (value) => (
        <span className="inline-flex px-2 py-1 text-xs font-medium rounded-md bg-blue-100 text-blue-800">
          {value || 'Uncategorized'}
        </span>
      )
    },
    {
      key: 'size',
      label: 'Size',
      sortable: true,
      render: (value) => (
        <span className="text-gray-700 font-mono">{value || '-'}</span>
      )
    },
    {
      key: 'dimensions',
      label: 'Dimensions (mm)',
      sortable: false,
      render: (value, record) => (
        <span className="text-gray-700 text-sm">
          {record.width_mm && record.height_mm 
            ? `${record.width_mm} × ${record.height_mm}`
            : '-'
          }
        </span>
      )
    },
    {
      key: 'weight_gsm',
      label: 'Weight (GSM)',
      sortable: true,
      render: (value) => (
        <span className="text-gray-700">
          {value ? `${value}gsm` : '-'}
        </span>
      )
    },
    {
      key: 'price_per_sheet',
      label: 'Price/Sheet',
      sortable: true,
      render: (value) => (
        <span className="text-green-600 font-medium">
          {value ? `£${parseFloat(value).toFixed(4)}` : '-'}
        </span>
      )
    },
    {
      key: 'sheets_per_pack',
      label: 'Sheets/Pack',
      sortable: true,
      render: (value) => (
        <span className="text-gray-700">
          {value || 1}
        </span>
      )
    },
    {
      key: 'display_tier',
      label: 'Display Tier',
      sortable: true,
      render: (value) => {
        if (!value) return <span className="text-gray-400">-</span>;

        const tierColors = {
          'premium': 'bg-purple-100 text-purple-800',
          'standard': 'bg-green-100 text-green-800',
          'economy': 'bg-yellow-100 text-yellow-800',
          'budget': 'bg-gray-100 text-gray-800'
        };

        const colorClass = tierColors[value.toLowerCase()] || 'bg-gray-100 text-gray-800';

        return (
          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-md ${colorClass}`}>
            {value}
          </span>
        );
      }
    },
    {
      key: 'use_fixed_pricing',
      label: 'Fixed Pricing',
      sortable: true,
      render: (value, record) => {
        if (!value) return <span className="text-gray-400">Standard</span>;
        return (
          <div className="flex items-center gap-2">
            <span className="inline-flex px-2 py-1 text-xs font-medium rounded-md bg-orange-100 text-orange-800">
              Fixed
            </span>
            {record.fixed_price_per_sheet && (
              <span className="text-green-600 font-medium">
                £{parseFloat(record.fixed_price_per_sheet).toFixed(2)}/sheet
              </span>
            )}
          </div>
        );
      }
    }
  ],

  // Form field definitions for the RecordModal
  fields: [
    {
      key: 'unique_id',
      label: 'Unique ID',
      type: 'text',
      required: true,
      placeholder: 'e.g., UDFS32115, A4-80GSM-WHITE',
      help: 'Unique identifier for this paper stock (from supplier system)',
      readonly: (isEdit) => isEdit // Can't change unique_id when editing
    },
    {
      key: 'name',
      label: 'Paper Name',
      type: 'text',
      required: true,
      placeholder: 'e.g., A4 White 80gsm Copy Paper',
      help: 'Descriptive name for this paper stock'
    },
    {
      key: 'category',
      label: 'Category',
      type: 'select',
      required: false,
      options: [
        { value: '', label: 'Select Category...' },
        { value: 'Copy Paper', label: 'Copy Paper' },
        { value: 'Card Stock', label: 'Card Stock' },
        { value: 'Specialty', label: 'Specialty' },
        { value: 'Envelopes', label: 'Envelopes' },
        { value: 'Labels', label: 'Labels' },
        { value: 'NCR', label: 'NCR (No Carbon Required)' },
        { value: 'Photo Paper', label: 'Photo Paper' },
        { value: 'Transparent', label: 'Transparent' }
      ],
      help: 'Product category for organization and filtering'
    },
    {
      key: 'description',
      label: 'Description',
      type: 'textarea',
      required: false,
      placeholder: 'Additional details about this paper stock...',
      rows: 2,
      help: 'Optional description with additional details'
    },
    {
      key: 'size',
      label: 'Paper Size',
      type: 'select',
      required: false,
      options: [
        { value: '', label: 'Select Size...' },
        { value: 'A4', label: 'A4' },
        { value: 'A3', label: 'A3' },
        { value: 'A5', label: 'A5' },
        { value: 'A6', label: 'A6' },
        { value: 'SRA3', label: 'SRA3' },
        { value: 'SRA4', label: 'SRA4' },
        { value: 'Letter', label: 'Letter' },
        { value: 'Legal', label: 'Legal' },
        { value: 'Tabloid', label: 'Tabloid' },
        { value: 'Custom', label: 'Custom Size' }
      ],
      help: 'Standard paper size (dimensions will be filled automatically)'
    },
    {
      key: 'width_mm',
      label: 'Width (mm)',
      type: 'number',
      required: false,
      placeholder: '210',
      step: '0.1',
      help: 'Paper width in millimeters'
    },
    {
      key: 'height_mm',
      label: 'Height (mm)',
      type: 'number',
      required: false,
      placeholder: '297',
      step: '0.1',
      help: 'Paper height in millimeters'
    },
    {
      key: 'weight_gsm',
      label: 'Weight (GSM)',
      type: 'number',
      required: false,
      placeholder: '80',
      step: '1',
      help: 'Paper weight in grams per square meter'
    },
    {
      key: 'cost',
      label: 'Cost',
      type: 'number',
      required: false,
      placeholder: '12.50',
      step: '0.01',
      help: 'Total cost for the pricing quantity'
    },
    {
      key: 'price_per',
      label: 'Price Per (Quantity)',
      type: 'number',
      required: false,
      placeholder: '500',
      step: '1',
      help: 'Number of sheets the cost applies to'
    },
    {
      key: 'sheets_per_pack',
      label: 'Sheets Per Pack',
      type: 'number',
      required: false,
      placeholder: '500',
      step: '1',
      defaultValue: 1,
      help: 'Number of sheets in a standard pack'
    },
    {
      key: 'markup_percent',
      label: 'Markup Percentage',
      type: 'number',
      required: false,
      placeholder: '25',
      step: '0.1',
      help: 'Markup percentage applied to cost'
    },
    {
      key: 'display_tier',
      label: 'Display Tier',
      type: 'select',
      required: false,
      options: [
        { value: '', label: 'Select Tier...' },
        { value: 'Premium', label: 'Premium' },
        { value: 'Standard', label: 'Standard' },
        { value: 'Economy', label: 'Economy' },
        { value: 'Budget', label: 'Budget' }
      ],
      help: 'Quality/price tier for customer selection'
    },
    {
      key: 'use_fixed_pricing',
      label: 'Use Fixed Pricing',
      type: 'checkbox',
      required: false,
      defaultValue: false,
      help: 'Enable fixed per-sheet pricing for premium papers (bypasses pricing curve)'
    },
    {
      key: 'fixed_price_per_sheet',
      label: 'Fixed Price Per Sheet (£)',
      type: 'number',
      required: false,
      placeholder: '1.20',
      step: '0.01',
      help: 'Fixed sales price per sheet for premium papers (only applies if "Use Fixed Pricing" is enabled)',
      visible: (record) => record?.use_fixed_pricing === true || record?.use_fixed_pricing === 1
    },
    {
      key: 'allowed_job_types',
      label: 'Allowed Job Types',
      type: 'multi-select',
      required: false,
      placeholder: 'Leave empty to allow all job types',
      options: [
        { value: 'business-card', label: 'Business Card' },
        { value: 'flyer', label: 'Flyer' },
        { value: 'letterhead', label: 'Letterhead' },
        { value: 'booklet', label: 'Booklet' },
        { value: 'brochure', label: 'Brochure' },
        { value: 'postcard', label: 'Postcard' },
        { value: 'notepad', label: 'Notepad' },
        { value: 'wirebound-books', label: 'Wirebound Books' },
        { value: 'perfect-bound-books', label: 'Perfect-Bound Books' },
        { value: 'pads', label: 'Pads' }
      ],
      help: 'Leave empty to allow this paper for all job types. Select specific types to restrict usage.'
    },
    {
      key: 'pricing_qty',
      label: 'Pricing Quantity',
      type: 'text',
      required: false,
      placeholder: 'e.g., per sheet, per pack, per ream',
      help: 'Description of how this item is priced'
    },
    {
      key: 'pl_supplier_id',
      label: 'Supplier ID',
      type: 'number',
      required: false,
      placeholder: '12345',
      help: 'Supplier identifier in procurement system'
    },
    {
      key: 'pl_id',
      label: 'PL ID',
      type: 'number',
      required: false,
      placeholder: '67890',
      help: 'Product line identifier'
    },
    {
      key: 'ncr_layer',
      label: 'NCR Layer',
      type: 'select',
      required: false,
      options: [
        { value: '', label: 'Not NCR Paper' },
        { value: 'Top', label: 'Top (White)' },
        { value: 'Middle', label: 'Middle (Yellow)' },
        { value: 'Bottom', label: 'Bottom (Pink)' }
      ],
      help: 'For NCR (No Carbon Required) papers only'
    }
  ],

  // Transform functions for API communication
  transformForApi: (data) => {
    // Calculate derived fields
    const transformed = { ...data };
    
    // Calculate price per sheet if we have cost and price_per
    if (transformed.cost && transformed.price_per) {
      transformed.price_per_sheet = parseFloat(transformed.cost) / parseInt(transformed.price_per);
    }
    
    // Set price_per_pack equal to cost for most cases
    if (transformed.cost) {
      transformed.price_per_pack = parseFloat(transformed.cost);
    }
    
    // If sheets_per_pack is not set, use price_per as default
    if (!transformed.sheets_per_pack && transformed.price_per) {
      transformed.sheets_per_pack = parseInt(transformed.price_per);
    }
    
    // Generate ID from unique_id if not provided
    if (transformed.unique_id && !transformed.id) {
      transformed.id = transformed.unique_id.toLowerCase().replace(/[^a-z0-9]/g, '-');
    }
    
    // Ensure numeric fields are properly formatted
    const numericFields = [
      'width_mm', 'height_mm', 'weight_gsm', 'cost', 'price_per_sheet', 
      'price_per_pack', 'markup_percent', 'packed_qty_only'
    ];
    numericFields.forEach(field => {
      if (transformed[field] !== undefined && transformed[field] !== '') {
        transformed[field] = parseFloat(transformed[field]) || 0;
      }
    });
    
    const integerFields = ['sheets_per_pack', 'price_per', 'pl_supplier_id', 'pl_id'];
    integerFields.forEach(field => {
      if (transformed[field] !== undefined && transformed[field] !== '') {
        transformed[field] = parseInt(transformed[field]) || null;
      }
    });
    
    return transformed;
  },

  transformFromApi: (data) => {
    // Convert data from API format to form format
    const transformed = { ...data };
    
    // Ensure all numeric fields are properly formatted for forms
    const numericFields = [
      'width_mm', 'height_mm', 'weight_gsm', 'cost', 'price_per_sheet', 
      'price_per_pack', 'markup_percent', 'packed_qty_only'
    ];
    numericFields.forEach(field => {
      if (transformed[field] !== null && transformed[field] !== undefined) {
        transformed[field] = parseFloat(transformed[field]);
      }
    });
    
    const integerFields = ['sheets_per_pack', 'price_per', 'pl_supplier_id', 'pl_id'];
    integerFields.forEach(field => {
      if (transformed[field] !== null && transformed[field] !== undefined) {
        transformed[field] = parseInt(transformed[field]);
      }
    });
    
    return transformed;
  },

  // Search and filter options
  searchFields: ['name', 'unique_id', 'description', 'category'],
  
  // Default sort
  defaultSort: { field: 'category', direction: 'asc' },
  
  // Validation rules
  validation: {
    unique_id: {
      required: true,
      pattern: /^[A-Z0-9-_]+$/i,
      message: 'Unique ID should contain only letters, numbers, hyphens, and underscores'
    },
    name: {
      required: true,
      minLength: 2,
      message: 'Name must be at least 2 characters long'
    },
    cost: {
      min: 0,
      message: 'Cost must be a positive number'
    },
    price_per: {
      min: 1,
      message: 'Price per quantity must be at least 1'
    },
    sheets_per_pack: {
      min: 1,
      message: 'Sheets per pack must be at least 1'
    },
    width_mm: {
      min: 0,
      message: 'Width must be a positive number'
    },
    height_mm: {
      min: 0,
      message: 'Height must be a positive number'
    },
    weight_gsm: {
      min: 0,
      message: 'Weight must be a positive number'
    }
  }
};

// Export helper functions
export const transformPaperStockForApi = paperStocksTableConfig.transformForApi;
export const transformPaperStockFromApi = paperStocksTableConfig.transformFromApi;